/**
 * detectScript.tsx - Script Detection Utility
 * 
 * Detects if text contains Arabic or Kurdish characters.
 * Both languages use the Arabic script, so we detect Arabic script ranges.
 */

/**
 * Detects if a string contains Arabic or Kurdish characters
 * @param text - The text to analyze
 * @returns true if the text contains Arabic/Kurdish characters, false otherwise
 */
export function containsArabicOrKurdish(text: string): boolean {
  if (!text || typeof text !== 'string') {
    return false;
  }

  // Arabic script Unicode ranges:
  // - Arabic (U+0600–U+06FF)
  // - Arabic Supplement (U+0750–U+077F)
  // - Arabic Extended-A (U+08A0–U+08FF)
  // - Arabic Presentation Forms-A (U+FB50–U+FDFF)
  // - Arabic Presentation Forms-B (U+FE70–U+FEFF)
  const arabicPattern = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
  
  return arabicPattern.test(text);
}

/**
 * Extracts text content from HTML string (removes HTML tags)
 * @param html - HTML string to extract text from
 * @returns Plain text content
 */
export function extractTextFromHtml(html: string): string {
  if (!html || typeof html !== 'string') {
    return '';
  }

  // Create a temporary DOM element to parse HTML and extract text
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  return tempDiv.textContent || tempDiv.innerText || '';
}

/**
 * Detects if HTML content contains Arabic or Kurdish characters
 * @param html - HTML string to analyze
 * @returns true if the HTML content contains Arabic/Kurdish characters, false otherwise
 */
export function htmlContainsArabicOrKurdish(html: string): boolean {
  const text = extractTextFromHtml(html);
  return containsArabicOrKurdish(text);
}

